import type { LocationQuery, RouteLocationNamedRaw } from 'vue-router'
import { addDays, isValid, parse } from 'date-fns'
import type { AddressTimeline, TimelineBlock, TimelineClosure, TimelineInterval } from '@apiTypes'
import { GateClosureReasonEnum } from '@apiTypes'
import { BOOKING_TIMELINE_DATE_QUERY_KEY, BOOKING_TIMELINE_FOCUS_ADDRESS_QUERY_KEY, BOOKING_TIMELINE_FOCUS_BOOKING_QUERY_KEY, BOOKING_TIMELINE_FOCUS_GATE_QUERY_KEY, BOOKING_TIMELINE_SHOW_REVOKED_DEFAULT, BOOKING_TIMELINE_SHOW_REVOKED_QUERY_KEY, BLOCK_ROW_HEIGHT_PX, DAY_END_MINUTE, DAY_START_MINUTE, GATE_ROW_MIN_ROWS, HOUR_WIDTH_PX, NOW_LABEL_WIDTH_MINUTES, OPENING_LEAD_MINUTES } from '@/views/time-slots/consts/booking-timeline'
import type {
  BookingTimelineAxisRange,
  BookingTimelineBlockLayout,
  BookingTimelineClosedRange,
  BookingTimelineFocus,
  BookingTimelineFrame,
  BookingTimelineGate,
  BookingTimelineLabelEdge,
  TimelineHourTick,
} from '@/views/time-slots/types/booking-timeline'
import { isNullOrUndefined } from '@/utils/common.utils'
import { formatDateInZone, formatDate, inZone, ISO_DATE_FORMAT, stringToDate } from '@/utils/date.utils'
import { formatFormTime } from '@/views/time-slots/utils/operating-hours.utils'
import { lastQueryId } from '@/utils/query.utils'
import { BookingScreenType } from '@/views/time-slots/types/booking-screens'

const MINUTES_PER_HOUR = 60
const MS_PER_MINUTE = 60_000
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export function minutesToPx(minutes: number): number {
  return (minutes / MINUTES_PER_HOUR) * HOUR_WIDTH_PX
}

export function trackWidthPx(axis: BookingTimelineAxisRange): number {
  return minutesToPx(axis.endMinute - axis.startMinute)
}

/** Pixels from the track's left edge. */
export function axisOffsetPx(minute: number, axis: BookingTimelineAxisRange): number {
  return minutesToPx(minute - axis.startMinute)
}

export function isMinuteOnAxis(minute: number, axis: BookingTimelineAxisRange): boolean {
  return minute >= axis.startMinute && minute <= axis.endMinute
}

/** Which track edge a label centred on the minute must hug to stay whole on the track. */
export function labelEdge(minute: number, axis: BookingTimelineAxisRange): BookingTimelineLabelEdge {
  const halfLabel = NOW_LABEL_WIDTH_MINUTES / 2

  if (minute < axis.startMinute + halfLabel)
    return 'start'

  return minute > axis.endMinute - halfLabel ? 'end' : 'center'
}

export function formatMinuteLabel(minute: number): string {
  return formatFormTime(Math.floor(minute / MINUTES_PER_HOUR), minute % MINUTES_PER_HOUR)
}

export function firstFullHourMinute(axis: BookingTimelineAxisRange): number {
  return Math.ceil(axis.startMinute / MINUTES_PER_HOUR) * MINUTES_PER_HOUR
}

/** Minutes since the address's day started, so a DST day runs 23 or 25 hours without a gap or a repeat. */
export function dayElapsedMinutes(instant: number, frame: BookingTimelineFrame): number {
  return (instant - Date.parse(frame.dayStartAt)) / MS_PER_MINUTE
}

export function dayLengthMinutes(frame: BookingTimelineFrame): number {
  return dayElapsedMinutes(Date.parse(frame.dayEndAt), frame)
}

/** The address's wall-clock time at a minute of its day; the day's end reads 24:00. */
export function formatDayMinuteLabel(minute: number, frame: BookingTimelineFrame): string {
  if (minute >= dayLengthMinutes(frame))
    return formatMinuteLabel(DAY_END_MINUTE)

  const instant = new Date(Date.parse(frame.dayStartAt) + minute * MS_PER_MINUTE)

  return formatMinuteLabel(wallClockMinute(inZone(instant, frame.timezoneName) ?? instant))
}

/** Every full hour inside the axis; the closing minute gets no label. */
export function buildHourTicks(axis: BookingTimelineAxisRange, frame: BookingTimelineFrame): TimelineHourTick[] {
  const ticks: TimelineHourTick[] = []

  for (let minute = firstFullHourMinute(axis); minute < axis.endMinute; minute += MINUTES_PER_HOUR)
    ticks.push({ minute, label: formatDayMinuteLabel(minute, frame) })

  return ticks
}

export function wallClockMinute(date: Date): number {
  return date.getHours() * MINUTES_PER_HOUR + date.getMinutes()
}

export function toIsoDay(date: Date): string {
  return formatDate(date, ISO_DATE_FORMAT)
}

export function parseIsoDay(iso: string): Date {
  return parse(iso, ISO_DATE_FORMAT, new Date())
}

/** Date-only arithmetic, so a DST transition never shifts the day. */
export function shiftIsoDay(iso: string, delta: number): string {
  return toIsoDay(addDays(parseIsoDay(iso), delta))
}

export function parseIsoDayOr(value: unknown, fallbackIso: string): string {
  if (typeof value !== 'string' || !ISO_DAY_PATTERN.test(value))
    return fallbackIso

  const parsed = parseIsoDay(value)

  return isValid(parsed) && toIsoDay(parsed) === value ? value : fallbackIso
}

export function gateVehicleTypeLabel(gate: BookingTimelineGate, t: (key: string) => string): string {
  if (!gate.specifiesVehicleTypes)
    return t('All address vehicle types')

  const names = gate.vehicleTypeRules
    .filter(rule => rule.isEnabled !== false)
    .map(rule => rule.vehicleType.name)

  return names.length ? names.join(', ') : t('No vehicle types')
}

function instantToDayMinute(iso: string, frame: BookingTimelineFrame): number {
  return Math.min(Math.max(dayElapsedMinutes(Date.parse(iso), frame), DAY_START_MINUTE), dayLengthMinutes(frame))
}

function intervalMinuteRange(interval: TimelineInterval, frame: BookingTimelineFrame): BookingTimelineAxisRange {
  const startMinute = instantToDayMinute(interval.startAt, frame)

  return { startMinute, endMinute: Math.max(instantToDayMinute(interval.endAt, frame), startMinute + 1) }
}

/** First opening to last closing (AC6); null when nothing opens and nothing is booked. */
export function axisMinuteRange(address: Pick<AddressTimeline, 'axis'> & BookingTimelineFrame): BookingTimelineAxisRange | null {
  return address.axis ? intervalMinuteRange(address.axis, address) : null
}

export function groupBlocksByGate(blocks: TimelineBlock[]): Map<number, TimelineBlock[]> {
  const byGate = new Map<number, TimelineBlock[]>()

  for (const block of blocks) {
    const gateBlocks = byGate.get(block.gate)

    if (gateBlocks)
      gateBlocks.push(block)
    else
      byGate.set(block.gate, [block])
  }

  return byGate
}

/** A block crossing midnight is clipped to the day, not wrapped. */
export function blockMinuteRange(block: TimelineBlock, frame: BookingTimelineFrame): Pick<BookingTimelineBlockLayout, 'startMinute' | 'endMinute'> {
  return intervalMinuteRange(block, frame)
}

/** A gate's unbookable time (AC7), clipped to the axis. */
export function closedBands(gate: BookingTimelineGate, frame: BookingTimelineFrame, axis: BookingTimelineAxisRange): BookingTimelineClosedRange[] {
  return gate.closedIntervals.flatMap((interval) => {
    const range = intervalMinuteRange(interval, frame)
    const startMinute = Math.max(range.startMinute, axis.startMinute)
    const endMinute = Math.min(range.endMinute, axis.endMinute)

    return startMinute < endMinute ? [{ startMinute, endMinute, closure: interval.closure }] : []
  })
}

export function gateClosureLabel(closure: Pick<TimelineClosure, 'reason' | 'name'>, t: (key: string) => string): string {
  if (closure.reason === GateClosureReasonEnum.EXCEPTION && closure.name)
    return closure.name

  const labels: Record<GateClosureReasonEnum, string> = {
    [GateClosureReasonEnum.OUTSIDE_OPERATING_HOURS]: t('Outside operating hours'),
    [GateClosureReasonEnum.EXCEPTION]: t('Operating hours exception'),
    [GateClosureReasonEnum.GATE_INACTIVE]: t('Gate inactive'),
  }

  return labels[closure.reason]
}

/** Initial scroll: an hour before now today (AC9), the first opening otherwise (AC10). */
export function openingOffsetMinute(nowMinute: number | null, axis: BookingTimelineAxisRange): number {
  if (nowMinute === null)
    return 0

  return Math.min(Math.max(nowMinute - OPENING_LEAD_MINUTES, axis.startMinute), axis.endMinute) - axis.startMinute
}

/** First-fit rows per overlap group; each group splits the lane height between its own rows. */
export function layoutTimelineBlocks(blocks: TimelineBlock[], frame: BookingTimelineFrame): BookingTimelineBlockLayout[] {
  const placed = blocks
    .map(block => ({ block, ...blockMinuteRange(block, frame) }))
    .sort((a, b) => a.startMinute - b.startMinute || a.endMinute - b.endMinute || a.block.id - b.block.id)

  const layouts: BookingTimelineBlockLayout[] = []
  let group: Omit<BookingTimelineBlockLayout, 'rows'>[] = []
  let rowEnds: number[] = []
  let groupEnd = Number.NEGATIVE_INFINITY

  const closeGroup = () => {
    layouts.push(...group.map(layout => ({ ...layout, rows: rowEnds.length })))
    group = []
    rowEnds = []
    groupEnd = Number.NEGATIVE_INFINITY
  }

  for (const item of placed) {
    if (item.startMinute >= groupEnd)
      closeGroup()

    const free = rowEnds.findIndex(end => end <= item.startMinute)
    const row = free === -1 ? rowEnds.length : free

    rowEnds[row] = item.endMinute
    groupEnd = Math.max(groupEnd, item.endMinute)
    group.push({ ...item, row })
  }

  closeGroup()

  return layouts
}

export function laneRowCount(layouts: BookingTimelineBlockLayout[]): number {
  return Math.max(GATE_ROW_MIN_ROWS, ...layouts.map(layout => layout.rows))
}

export function laneHeightPx(rows: number): number {
  return rows * BLOCK_ROW_HEIGHT_PX
}

export function formatTimelineBlockRange(startAt: string, endAt: string, timeZone: string): string {
  const start = stringToDate(startAt)
  const end = stringToDate(endAt)
  const sameDay = formatDateInZone(start, timeZone, ISO_DATE_FORMAT) === formatDateInZone(end, timeZone, ISO_DATE_FORMAT)

  return `${formatDateInZone(start, timeZone, 'HH:mm')} - ${formatDateInZone(end, timeZone, sameDay ? 'HH:mm' : 'dd.MM.yyyy HH:mm')}`
}

/** Show on Timeline's one-shot target. Without a valid address there is no focus; a malformed gate or booking is dropped. */
export function parseBookingTimelineFocus(query: LocationQuery): BookingTimelineFocus | null {
  const addressId = lastQueryId(query[BOOKING_TIMELINE_FOCUS_ADDRESS_QUERY_KEY])

  if (isNullOrUndefined(addressId))
    return null

  return {
    addressId,
    gateId: lastQueryId(query[BOOKING_TIMELINE_FOCUS_GATE_QUERY_KEY]) ?? undefined,
    bookingId: lastQueryId(query[BOOKING_TIMELINE_FOCUS_BOOKING_QUERY_KEY]) ?? undefined,
  }
}

/** `showRevoked` opts the day into revoked bookings, which the endpoint otherwise leaves out. */
export function bookingTimelineLocation(
  day: string | null | undefined,
  focus?: BookingTimelineFocus,
  showRevoked = BOOKING_TIMELINE_SHOW_REVOKED_DEFAULT,
): RouteLocationNamedRaw {
  return {
    name: 'bookings',
    query: {
      tab: BookingScreenType.Timeline,
      ...(day ? { [BOOKING_TIMELINE_DATE_QUERY_KEY]: day } : {}),
      ...(focus
        ? {
          [BOOKING_TIMELINE_FOCUS_ADDRESS_QUERY_KEY]: String(focus.addressId),
          [BOOKING_TIMELINE_FOCUS_GATE_QUERY_KEY]: focus.gateId?.toString(),
          [BOOKING_TIMELINE_FOCUS_BOOKING_QUERY_KEY]: focus.bookingId?.toString(),
        }
        : {}),
      ...(showRevoked === BOOKING_TIMELINE_SHOW_REVOKED_DEFAULT ? {} : { [BOOKING_TIMELINE_SHOW_REVOKED_QUERY_KEY]: String(showRevoked) }),
    },
  }
}
